import { Form } from "antd"
import { t } from "i18next"
import { Checkbox, RadioButtons, Segmented, SelectInput, Switch, TextInput, UploadDragger } from "components/common"
import { localisedValue } from "utils"
import { isEffectivelyMandatory, NEXT_PROPOSED_TASK_KEY } from "../utils"

const ChipsField = ({ field, formItemProps }) => {
  return (
    <RadioButtons
      options={field?.options}
      getValue={opt => opt?.value}
      {...formItemProps}
      onChange={e => {
        const v = e?.target?.value ?? e
        formItemProps.onChange?.(v)
      }}
    />
  )
}

const TextField = ({ field, formItemProps }) => {
  return (
    <TextInput
      placeholder={t("Enter {{label}}", { label: field.title })}
      maxLength={field.maxLength}
      lineCount={field.maxLength > 200 ? 4 : 1}
      style={field.maxLength > 200 ? { resize: "none" } : undefined}
      {...formItemProps}
    />
  )
}

const AttachmentField = ({ field, form, formItemProps }) => {
  const value = formItemProps?.value
  const fileList = Array.isArray(value) ? value : value != null ? [value] : []
  return (
    <UploadDragger
      name={field?.key}
      files={fileList}
      listType="picture"
      imageType="image/png, image/jpeg, image/jpg"
      maxCount={3}
      setFiles={cb => {
        const next = cb(fileList)
        form.setFieldValue(field?.key, next)
        formItemProps.onChange?.(next)
      }}
    />
  )
}

const ScaleField = ({ field, form, formItemProps }) => {
  const ratings = Array.from({ length: field?.scale?.max - field?.scale?.min + 1 }, (_, i) => field?.scale?.min + i)
  const ratingOptions = ratings?.map(v => ({ label: v, value: v }))
  const current = form.getFieldValue(field?.key) || {}
  const handleChange = (criteriaLabel, value) => {
    const next = { ...current, [criteriaLabel]: value }
    form.setFieldsValue({ [field.key]: next })
    formItemProps.onChange?.(next)
  }
  return (
    <>
      {field?.criteria?.map(criteria => {
        const label = localisedValue({ object: criteria, key: "label" })
        const segmentValue = current[label]
        return (
          <Form.Item key={label} label={label} className="mbe-4" required={isEffectivelyMandatory(field)}>
            <Segmented
              options={ratingOptions}
              value={segmentValue != null && segmentValue !== "" ? segmentValue : "__none__"}
              onChange={value => handleChange(label, value)}
            />
          </Form.Item>
        )
      })}
    </>
  )
}

const buildSelectProps = (field, formItemProps, taskOptions, taskOptionsLoading) => {
  const useTaskApi = field?.key === NEXT_PROPOSED_TASK_KEY
  return {
    placeholder: t("Enter {{label}}", { label: field?.title }),
    options: useTaskApi ? (taskOptions ?? []) : field?.options,
    getOptionValue: opt => opt?.value ?? opt?.id,
    getOptionLabel: opt => opt?.label ?? localisedValue({ object: opt, key: "name" }) ?? opt?.name,
    inputLoading: useTaskApi ? taskOptionsLoading : false,
    ...formItemProps,
  }
}

export const ReviewFormField = ({ field, form, taskOptions, taskOptionsLoading, value, onChange, ...rest }) => {
  const formItemProps = { value, onChange, ...rest }
  const selectProps = buildSelectProps(field, formItemProps, taskOptions, taskOptionsLoading)

  switch (field?.fieldType) {
    case "chip":
      return <ChipsField field={field} formItemProps={formItemProps} />
    case "multi_select":
      return <SelectInput {...selectProps} mode="multiple" />
    case "single_select":
    case "dropdown":
      return <SelectInput {...selectProps} />
    case "text":
      return <TextField field={field} formItemProps={formItemProps} />
    case "toggle":
      return <Switch {...formItemProps} />
    case "checkbox":
      return <Checkbox {...formItemProps}>{localisedValue({ object: field, key: "title" })}</Checkbox>
    case "attachment":
      return <AttachmentField field={field} form={form} formItemProps={formItemProps} />
    case "scale":
      return <ScaleField field={field} form={form} formItemProps={formItemProps} />
    default:
      return null
  }
}
